// Auth Models - Professional data structures for authentication
// Centralizes validation, normalization, and error handling

/**
 * Login credentials model
 */
export class LoginCredentials {
  constructor(email, password) {
    this.email = email;
    this.password = password;
  }

  validate() {
    const errors = [];
    
    if (!this.email || this.email.trim() === '') {
      errors.push('E-mail é obrigatório.');
    } else if (!this.isValidEmail(this.email)) {
      errors.push('E-mail inválido.');
    }
    
    if (!this.password || this.password.trim() === '') {
      errors.push('Senha é obrigatória.');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  toJSON() {
    return {
      email: this.email.trim().toLowerCase(),
      password: this.password
    };
  }
}

/**
 * Registration data model
 */
export class RegistrationData {
  constructor({ name, email, password, confirmPassword, document, docType, phone, agreeTerms }) {
    this.name = name;
    this.email = email;
    this.password = password;
    this.confirmPassword = confirmPassword;
    this.document = document;
    this.docType = docType;
    this.phone = phone;
    this.agreeTerms = agreeTerms;
  }

  validate() {
    const errors = [];
    
    // Name validation
    if (!this.name || this.name.trim() === '') {
      errors.push('Nome é obrigatório.');
    } else if (this.name.trim().length < 3) {
      errors.push('Nome deve ter pelo menos 3 caracteres.');
    }
    
    // Email validation
    if (!this.email || this.email.trim() === '') {
      errors.push('E-mail é obrigatório.');
    } else if (!this.isValidEmail(this.email)) {
      errors.push('E-mail inválido.');
    }
    
    // Document validation
    if (!this.document || this.document.trim() === '') {
      errors.push(`${this.docType} é obrigatório.`);
    } else if (this.docType === 'CPF' && !this.isValidCPF(this.document)) {
      errors.push('CPF inválido.');
    } else if (this.docType === 'CNPJ' && !this.isValidCNPJ(this.document)) {
      errors.push('CNPJ inválido.');
    }
    
    // Phone validation
    if (!this.phone || this.phone.trim() === '') {
      errors.push('Telefone é obrigatório.');
    } else if (this.phone.replace(/\D/g, '').length < 10) {
      errors.push('Telefone inválido.');
    }
    
    // Password validation
    if (!this.password || this.password.trim() === '') {
      errors.push('Senha é obrigatória.');
    } else if (this.password.length < 6) {
      errors.push('Senha deve ter pelo menos 6 caracteres.');
    }
    
    // Confirm password validation
    if (this.password !== this.confirmPassword) {
      errors.push('As senhas não coincidem.');
    }
    
    // Terms validation
    if (!this.agreeTerms) {
      errors.push('Você deve aceitar os termos de uso.');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  isValidCPF(cpf) {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return false;
    if (/^(\d)\1+$/.test(cleaned)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleaned.charAt(i)) * (10 - i);
    }
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleaned.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleaned.charAt(i)) * (11 - i);
    }
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    return digit === parseInt(cleaned.charAt(10));
  }

  isValidCNPJ(cnpj) {
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length !== 14) return false;
    if (/^(\d)\1+$/.test(cleaned)) return false;
    
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(cleaned.charAt(i)) * weights1[i];
    }
    let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (digit !== parseInt(cleaned.charAt(12))) return false;
    
    sum = 0;
    for (let i = 0; i < 13; i++) {
      sum += parseInt(cleaned.charAt(i)) * weights2[i];
    }
    digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return digit === parseInt(cleaned.charAt(13));
  }

  toJSON() {
    return {
      name: this.name.trim(),
      email: this.email.trim().toLowerCase(),
      password: this.password,
      document: this.document.replace(/\D/g, ''),
      docType: this.docType,
      phone: this.phone.replace(/\D/g, '')
    };
  }
}

/**
 * User model (normalized from API response)
 */
export class User {
  constructor(data) {
    this.id = data.id;
    this.email = data.email;
    this.name = data.name;
    this.phone = data.phone;
    this.document = data.document;
    this.docType = data.docType;
    this.createdAt = data.createdAt;
  }

  static fromJSON(json) {
    return new User(json);
  }
}

/**
 * Auth tokens model
 */
export class AuthTokens {
  constructor(accessToken, refreshToken, user) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.user = user instanceof User ? user : new User(user);
  }

  static fromJSON(json) {
    return new AuthTokens(json.accessToken, json.refreshToken, json.user);
  }

  toJSON() {
    return {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
      user: this.user
    };
  }
}

export default {
  LoginCredentials,
  RegistrationData,
  User,
  AuthTokens
};
